import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaBook, FaStar, FaHeart, FaLightbulb, FaSpinner, FaBrain, FaRobot } from 'react-icons/fa';
import { analyzeSongVerbs, generateVerbExercises, type VerbExercise, type VerbAnalysis } from '../../lib/verb';
import ChallengeFillBlanksExercise from './ChallengeFillBlanksExercise';
import { getUserChallengeSession } from '../../lib/challenges';
import { getCurrentUser } from '../../lib/auth';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
}

interface VerbConjugationExerciseProps {
  song: Song;
  onComplete: (results: any) => void;
  challengeMode?: boolean;
  sessionId?: string;
  expiresAt?: string;
  rewardPoints?: number;
  onExitChallenge?: () => void;
}

export default function VerbConjugationExercise({ 
  song, 
  onComplete, 
  challengeMode = false,
  sessionId,
  expiresAt,
  rewardPoints,
  onExitChallenge 
}: VerbConjugationExerciseProps) {
  const [questions, setQuestions] = useState<VerbExercise[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [startTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verbAnalysis, setVerbAnalysis] = useState<VerbAnalysis | null>(null);
  const [streak, setStreak] = useState(0);
  const [generatingExercises, setGeneratingExercises] = useState(false);
  const [userCurrentChallenge, setUserCurrentChallenge] = useState<any>(null);
  const user = getCurrentUser();

  useEffect(() => {
    generateAIExercises();
    if (user) {
      checkUserChallenge();
    }
  }, []);

  const checkUserChallenge = async () => {
    if (!user) return;
    try {
      const currentChallenge = await getUserChallengeSession(user.id);
      setUserCurrentChallenge(currentChallenge);
    } catch (error) {
      console.error('Error checking user challenge:', error);
    }
  };

  // If user is in a challenge and this is the challenge song, show challenge mode
  if (userCurrentChallenge && userCurrentChallenge.songId === song.id && !challengeMode) {
    return (
      <ChallengeFillBlanksExercise
        song={song}
        sessionId={userCurrentChallenge.sessionId}
        expiresAt={userCurrentChallenge.expiresAt}
        rewardPoints={100}
        onComplete={onComplete}
        onExit={onExitChallenge || (() => {})}
      />
    );
  }

  const generateAIExercises = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Step 1: Analyze song lyrics to extract verbs
      console.log('Analyzing song verbs with AI...');
      const analysis = await analyzeSongVerbs(song.song_lyrics, song.language);
      setVerbAnalysis(analysis);
      
      if (!analysis.verbs || analysis.verbs.length === 0) {
        throw new Error('No verbs found in the song lyrics. The AI couldn\'t identify conjugated verbs to practice with.');
      }

      // Step 2: Generate exercises based on the analysis
      setGeneratingExercises(true);
      console.log('Generating verb exercises with AI...');
      const exercises = await generateVerbExercises(analysis, 8, song.language);
      
      if (!exercises || exercises.length === 0) {
        throw new Error('Failed to generate exercises. Please try again.');
      }

      setQuestions(exercises);
      console.log(`Generated ${exercises.length} AI-powered verb exercises`);
    } catch (error: any) {
      console.error('Error generating AI exercises:', error);
      setError(error.message || 'Failed to generate verb exercises. Please try again.');
      
      // Fallback to basic exercises if AI fails
      generateFallbackExercises();
    } finally {
      setLoading(false);
      setGeneratingExercises(false);
    }
  };

  const generateFallbackExercises = () => {
    console.log('Generating fallback exercises...');
    
    // Basic Spanish verbs for fallback
    const basicVerbs = [
      { infinitive: 'ser', conjugation: 'soy', subject: 'yo', tense: 'presente' },
      { infinitive: 'estar', conjugation: 'estás', subject: 'tú', tense: 'presente' },
      { infinitive: 'tener', conjugation: 'tiene', subject: 'él/ella', tense: 'presente' },
      { infinitive: 'hacer', conjugation: 'hacemos', subject: 'nosotros', tense: 'presente' },
      { infinitive: 'ir', conjugation: 'van', subject: 'ellos/ellas', tense: 'presente' },
      { infinitive: 'querer', conjugation: 'quiero', subject: 'yo', tense: 'presente' },
      { infinitive: 'poder', conjugation: 'puedes', subject: 'tú', tense: 'presente' },
      { infinitive: 'decir', conjugation: 'dice', subject: 'él/ella', tense: 'presente' }
    ];

    const fallbackQuestions: VerbExercise[] = basicVerbs.map((verb, index) => ({
      verb: verb.conjugation,
      infinitive: verb.infinitive,
      subject: verb.subject,
      tense: verb.tense,
      correctAnswer: verb.conjugation,
      options: generateBasicOptions(verb.infinitive, verb.conjugation, verb.subject),
      explanation: `"${verb.infinitive}" se conjuga como "${verb.conjugation}" con "${verb.subject}" en ${verb.tense}.`,
      difficulty: 'easy' as const,
      context: `Practica la conjugación del verbo "${verb.infinitive}" con "${verb.subject}".`
    }));

    setQuestions(fallbackQuestions);
  };

  const generateBasicOptions = (infinitive: string, correct: string, subject: string): string[] => {
    const root = infinitive.slice(0, -2);
    const ending = infinitive.slice(-2);
    
    let options = [correct];
    
    // Generate wrong options based on verb ending
    if (ending === 'ar') {
      const arEndings = ['o', 'as', 'a', 'amos', 'an'];
      arEndings.forEach(end => {
        const option = root + end;
        if (option !== correct && options.length < 4) {
          options.push(option);
        }
      });
    } else if (ending === 'er') {
      const erEndings = ['o', 'es', 'e', 'emos', 'en'];
      erEndings.forEach(end => {
        const option = root + end;
        if (option !== correct && options.length < 4) {
          options.push(option);
        }
      });
    } else if (ending === 'ir') {
      const irEndings = ['o', 'es', 'e', 'imos', 'en'];
      irEndings.forEach(end => {
        const option = root + end;
        if (option !== correct && options.length < 4) {
          options.push(option);
        }
      });
    }

    // Fill remaining slots if needed
    while (options.length < 4) {
      options.push(infinitive + Math.random().toString(36).substr(2, 2));
    }

    // Shuffle options
    return options.sort(() => Math.random() - 0.5);
  };

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswer(answer);
  };

  const handleNextQuestion = () => {
    const question = questions[currentQuestion];
    const isCorrect = selectedAnswer === question.correctAnswer;
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
    }

    const newResult = {
      questionId: question.verb + currentQuestion,
      verb: question.verb,
      infinitive: question.infinitive,
      subject: question.subject,
      tense: question.tense,
      correctAnswer: question.correctAnswer,
      userAnswer: selectedAnswer,
      isCorrect,
      explanation: question.explanation,
      difficulty: question.difficulty
    };

    const newResults = [...results, newResult];
    setResults(newResults);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer('');
    } else {
      finishExercise(newResults);
    }
  };

  const finishExercise = (finalResults: any[]) => {
    const correctAnswers = finalResults.filter(r => r.isCorrect).length;
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    
    setShowResults(true);
    onComplete({
      score: correctAnswers,
      totalQuestions: questions.length,
      timeTaken,
      answers: finalResults
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'hard': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-400/20 text-green-400';
      case 'medium': return 'bg-yellow-400/20 text-yellow-400';
      case 'hard': return 'bg-red-400/20 text-red-400';
      default: return 'bg-gray-400/20 text-gray-400';
    }
  };

  const renderLoading = () => (
    <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
      <div className="space-y-4 sm:space-y-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center mx-auto animate-pulse">
          <FaBrain className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
            {generatingExercises ? '🤖 Generando Ejercicios con IA' : '🔍 Analizando Letras con IA'}
          </h3>
          <p className="text-white/80 text-sm sm:text-base px-4">
            {generatingExercises 
              ? 'Creando ejercicios personalizados basados en los verbos de la canción...'
              : 'Identificando verbos y conjugaciones en las letras de la canción...'
            }
          </p>
        </div>

        <div className="flex items-center justify-center space-x-2">
          <FaSpinner className="animate-spin w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
          <span className="text-white/70 text-sm sm:text-base">
            {generatingExercises ? 'Generando...' : 'Analizando...'}
          </span>
        </div>

        {verbAnalysis && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-purple-400/30">
            <p className="text-purple-200 text-xs sm:text-sm">
              ✅ Encontrados {verbAnalysis.totalVerbs} verbos en la canción
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderError = () => (
    <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
      <div className="space-y-4 sm:space-y-6">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
          <FaTimes className="w-8 h-8 sm:w-10 sm:h-10 text-red-400" />
        </div>
        
        <div className="space-y-2 sm:space-y-3">
          <h3 className="text-lg sm:text-xl font-bold text-white">Error al Generar Ejercicios</h3>
          <p className="text-red-200 text-sm sm:text-base px-4">{error}</p>
        </div>

        <button
          onClick={generateAIExercises}
          className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-lg sm:rounded-xl hover:from-purple-500 hover:to-pink-500 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = questions[currentQuestion];
    if (!question) return null;

    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Animated Progress Header */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <FaStar className="text-yellow-400 animate-pulse w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-base sm:text-lg">Pregunta {currentQuestion + 1}</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaHeart className="text-red-400 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-sm sm:text-base">Racha: {streak}</span>
            </div>
            <div className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-bold ${getDifficultyBadge(question.difficulty)}`}>
              {question.difficulty.toUpperCase()}
            </div>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="relative w-full h-2 sm:h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-purple-400 to-pink-400 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse rounded-full"></div>
            </div>
            <div className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-xs font-bold text-white">
              {Math.round(progress)}%
            </div>
          </div>
        </div>

        {/* AI Badge */}
        <div className="flex justify-center">
          <div className="flex items-center px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-400/20 to-pink-400/20 backdrop-blur-lg rounded-full border border-purple-400/30">
            <FaRobot className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400 mr-2" />
            <span className="text-purple-200 text-xs sm:text-sm font-medium">Ejercicio Generado por IA</span>
          </div>
        </div>

        {/* Main Question Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-purple-400/30 shadow-2xl">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4 animate-fade-in">
              Conjuga el verbo "{question.infinitive}"
            </h3>
            <div className="space-y-2 sm:space-y-3">
              <p className="text-white/80 text-sm sm:text-base">
                <span className="font-semibold">Sujeto:</span> {question.subject} | 
                <span className="font-semibold ml-2">Tiempo:</span> {question.tense}
              </p>
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswerSelect(option)}
                className={`p-3 sm:p-4 lg:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
                  selectedAnswer === option
                    ? 'border-purple-400 bg-purple-400/20 shadow-lg'
                    : 'border-white/20 bg-white/5 hover:border-purple-400/50 hover:bg-white/10'
                }`}
              >
                <span className="font-bold text-white text-sm sm:text-base lg:text-lg">{option}</span>
              </button>
            ))}
          </div>

          {/* Study Tip - General guidance without revealing answers */}
          <div className="text-center">
            <div className="p-3 sm:p-4 bg-blue-400/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-blue-400/20">
              <p className="text-blue-200 text-xs sm:text-sm">
                💡 Recuerda: Los verbos cambian según el sujeto y el tiempo verbal
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            onClick={handleNextQuestion}
            disabled={!selectedAnswer}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-xl sm:rounded-2xl hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            {currentQuestion < questions.length - 1 ? 'Siguiente Pregunta' : 'Terminar Ejercicio'}
            <FaCheck className="ml-2 sm:ml-3 w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const correctAnswers = results.filter(r => r.isCorrect).length;
    const percentage = Math.round((correctAnswers / questions.length) * 100);

    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
        {/* Celebration Header */}
        <div className="space-y-4 sm:space-y-6">
          <div className="text-6xl sm:text-7xl lg:text-8xl animate-bounce">📝</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">¡Excelente Trabajo!</h2>
          <div className="text-6xl sm:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent animate-pulse">
            {percentage}%
          </div>
          <p className="text-white/80 text-base sm:text-lg lg:text-xl px-4">
            Obtuviste {correctAnswers} de {questions.length} conjugaciones correctas
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-purple-400/20 rounded-full">
              <FaStar className="text-purple-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Racha Final: {streak}</span>
            </div>
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-pink-400/20 rounded-full">
              <FaRobot className="text-pink-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Ejercicios IA</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-purple-400/30">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Revisa tus Respuestas</h3>
          <div className="space-y-3 sm:space-y-4 max-h-48 sm:max-h-60 overflow-y-auto">
            {results.map((result, index) => (
              <div key={index} className="text-left bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-white text-sm sm:text-base">
                      {result.infinitive} → {result.subject} ({result.tense})
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyBadge(result.difficulty)}`}>
                      {result.difficulty}
                    </span>
                  </div>
                  {result.isCorrect ? (
                    <FaCheck className="text-green-400 w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    <FaTimes className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Correcto:</span>
                    <span className="font-medium text-green-400 ml-2 text-xs sm:text-sm">{result.correctAnswer}</span>
                  </div>
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Tu respuesta:</span>
                    <span className={`font-medium ml-2 text-xs sm:text-sm ${result.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {result.userAnswer}
                    </span>
                  </div>
                  {result.explanation && (
                    <div className="mt-2 p-2 bg-white/5 rounded-lg">
                      <span className="text-white/60 text-xs">💡 {result.explanation}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-xl sm:rounded-2xl hover:from-purple-500 hover:to-pink-500 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    );
  };

  if (loading) {
    return renderLoading();
  }

  if (error && questions.length === 0) {
    return renderError();
  }

  return (
    <div className="min-h-[500px] sm:min-h-[600px]">
      {showResults ? renderResults() : renderQuestion()}
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}